import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowDown, ChevronLeft, MessageCircle, Share2 } from 'lucide-react';
import { offlineListings } from './offlineListingData';
import ImageWithLoader from '../components/ImageWithLoader';

function OfflineDetails() {
    const navigate = useNavigate();
    const { listingId } = useParams();
    const normalizedListingId = decodeURIComponent(listingId || '');
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const [showDesktopPopup, setShowDesktopPopup] = useState(false);
    const [swipeOffsetX, setSwipeOffsetX] = useState(0);
    const [isSwiping, setIsSwiping] = useState(false);
    const swipeStartRef = useRef({ x: 0, y: 0 });

    const listing = useMemo(() => {
        return offlineListings.find((item) => String(item.id) === String(normalizedListingId));
    }, [normalizedListingId]);

    const images = listing?.gallery?.length ? listing.gallery : listing ? [listing.image] : [];

    useEffect(() => {
        window.scrollTo(0, 0);
        setActiveImageIndex(0);
        setSwipeOffsetX(0);
        setIsSwiping(false);
    }, [normalizedListingId]);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(min-width: 769px)');

        const handleScreenChange = (event) => {
            setShowDesktopPopup(event.matches);
        };

        setShowDesktopPopup(mediaQuery.matches);

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleScreenChange);
        } else {
            mediaQuery.addListener(handleScreenChange);
        }

        return () => {
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', handleScreenChange);
            } else {
                mediaQuery.removeListener(handleScreenChange);
            }
        };
    }, []);

    const handleShare = async () => {
        if (!listing) {
            return;
        }

        const shareMessage = `Hi, I found a good deal.\nTitle: ${listing.title}\nLink: ${window.location.href}`;
        const shareData = {
            title: 'Hot Deals',
            text: `Hi, I found a good deal. ${listing.title}`,
            url: window.location.href
        };

        if (navigator.share) {
            try {
                await navigator.share(shareData);
            } catch (error) {
                // Ignore canceled share action.
            }
            return;
        }

        if (navigator.clipboard) {
            try {
                await navigator.clipboard.writeText(shareMessage);
            } catch (error) {
                // Ignore clipboard errors.
            }
        }
    };

    if (!listing) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#f4f5f7] px-4">
                <div className="w-full max-w-[420px] rounded-2xl bg-white p-6 text-center shadow-[0_10px_24px_rgba(20,38,53,0.15)]">
                    <h2 className="text-xl font-semibold text-[#243445]">Listing Not Found</h2>
                    <button
                        type="button"
                        onClick={() => navigate('/offline-listing')}
                        className="mt-4 rounded-[10px] bg-[#1d85d6] px-4 py-2 text-sm font-semibold text-white"
                    >
                        Back to Listings
                    </button>
                </div>
            </div>
        );
    }

    const goToNextImage = () => {
        if (images.length < 2) {
            return;
        }
        setActiveImageIndex((previous) => (previous + 1) % images.length);
    };

    const goToPreviousImage = () => {
        if (images.length < 2) {
            return;
        }
        setActiveImageIndex((previous) => (previous - 1 + images.length) % images.length);
    };

    const handleImageTouchStart = (event) => {
        const touch = event.touches[0];
        swipeStartRef.current = { x: touch.clientX, y: touch.clientY };
        setIsSwiping(false);
        setSwipeOffsetX(0);
    };

    const handleImageTouchMove = (event) => {
        if (images.length < 2) {
            return;
        }

        const touch = event.touches[0];
        const deltaX = touch.clientX - swipeStartRef.current.x;
        const deltaY = touch.clientY - swipeStartRef.current.y;
        const isHorizontalGesture = Math.abs(deltaX) > Math.abs(deltaY);

        if (!isHorizontalGesture) {
            return;
        }

        setIsSwiping(true);
        setSwipeOffsetX(deltaX);
    };

    const handleImageTouchEnd = (event) => {
        if (images.length < 2) {
            setSwipeOffsetX(0);
            setIsSwiping(false);
            return;
        }

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - swipeStartRef.current.x;
        const deltaY = touch.clientY - swipeStartRef.current.y;
        const isHorizontalSwipe =
            Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY);

        if (!isHorizontalSwipe) {
            setSwipeOffsetX(0);
            setIsSwiping(false);
            return;
        }

        if (deltaX < 0) {
            goToNextImage();
        } else {
            goToPreviousImage();
        }

        setSwipeOffsetX(0);
        setIsSwiping(false);
    };

    const handleImageTouchCancel = () => {
        setSwipeOffsetX(0);
        setIsSwiping(false);
    };

    if (showDesktopPopup) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[rgba(15,23,35,0.92)] px-6 text-center">
                <div className="w-full max-w-[420px] rounded-2xl bg-white px-6 py-7 shadow-[0_14px_30px_rgba(20,38,53,0.2)]">
                    <h2 className="mb-2 text-[26px] font-semibold text-[#243445]">Mobile Only Access</h2>
                    <p className="text-base leading-[1.4] text-[#536172]">
                        This page is available only on mobile devices. Please open it on your phone.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#f4f5f7] text-[#1f2937]">
            <div className="mx-auto w-full max-w-[560px] pb-7">
                <header className="flex h-[68px] items-center justify-between border-b border-[#d6dbe1] bg-[#f9f9fa] px-3">
                    <button type="button" onClick={() => navigate('/offline-listing')} className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#5f6d7c]" aria-label="Back">
                        <ChevronLeft size={22} />
                    </button>
                    <h1 className="m-0 text-[17px] font-medium uppercase tracking-[0.6px] text-[#1d85d6] sm:text-[20px]">Hot Deals</h1>
                    <button type="button" className="flex h-6 w-7 flex-col justify-between bg-transparent p-0" aria-label="Filters">
                        <span className="h-1 w-full rounded bg-[#80858d]" />
                        <span className="h-1 w-[70%] self-end rounded bg-[#80858d]" />
                        <span className="h-1 w-[40%] self-end rounded bg-[#80858d]" />
                    </button>
                </header>

                <main className="px-2 pb-24 pt-5">
                    <section className="rounded-[14px] bg-transparent">
                        <div className="overflow-hidden rounded-[14px] p-0">
                            <div
                                className="relative overflow-hidden rounded-[12px]"
                                onTouchStart={handleImageTouchStart}
                                onTouchMove={handleImageTouchMove}
                                onTouchEnd={handleImageTouchEnd}
                                onTouchCancel={handleImageTouchCancel}
                            >
                                <div
                                    className={`flex ${isSwiping ? '' : 'transition-transform duration-300 ease-out'}`}
                                    style={{
                                        transform: `translateX(calc(${-activeImageIndex * 100}% + ${swipeOffsetX}px))`
                                    }}
                                >
                                    {images.map((imageUrl, index) => (
                                        <div key={`${imageUrl}-${index}`} className="w-full shrink-0">
                                            <ImageWithLoader
                                                src={imageUrl}
                                                alt={`${listing.title} - ${index + 1}`}
                                                className="block aspect-[16/9] w-full object-cover"
                                                loading={index === 0 ? 'eager' : 'lazy'}
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-[rgba(0,0,0,0.35)] px-2 py-1">
                                    {images.map((item, index) => (
                                        <button
                                            key={`${item}-${index}`}
                                            type="button"
                                            onClick={() => setActiveImageIndex(index)}
                                            className={`h-2 w-2 rounded-full ${index === activeImageIndex ? 'bg-white' : 'bg-[rgba(255,255,255,0.5)]'}`}
                                            aria-label={`Show image ${index + 1}`}
                                        />
                                    ))}
                                </div>
                            </div>

                            <div className="px-1 pb-2 pt-3">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="rounded-r-[8px] bg-[#ff321d] px-3 py-1 text-[10px] font-bold uppercase text-white">Limited Time Offer</span>
                                    <span className="inline-flex items-center gap-1 rounded-[999px] bg-[#45cd32] px-3 py-1 text-white">
                                        <small className="text-[10px] font-semibold">Price Reduced</small>
                                        <strong className="inline-flex items-center text-[24px] font-semibold leading-none">
                                            {listing.discountPercent || 0}%
                                            <ArrowDown size={18} strokeWidth={3} />
                                        </strong>
                                    </span>
                                </div>

                                <div className="mt-2 flex items-center justify-between gap-2">
                                    <p className="m-0 text-[16px] font-normal text-[#7f8791] line-through sm:text-sm">{listing.oldPrice}</p>
                                    <p className="m-0 font-bold text-[#243445]" style={{ fontSize: '24px' }}>
                                        {listing.newPrice}
                                    </p>
                                </div>

                                {/* <p className="mt-3 text-[12px] font-medium leading-[1.35] text-[#596676]">{listing.title}</p> */}
                                {listing.referenceNo && (
                                    <p className="mt-1 text-xs font-medium uppercase tracking-[0.4px] text-[#8b95a1]">
                                        Ref: {listing.referenceNo}
                                    </p>
                                )}
                            </div>
                        </div>
                    </section>

                    <section className="px-1 pb-7 pt-6">
                        <p className="mt-3 text-[20px] font-medium leading-[1.35] text-[#2d3f53]">{listing.descriptionTitle}</p>
                        <br />
                        <h2 className="m-0 text-[18px] font-semibold text-[#25384b]">Description</h2>
                        <div className="mt-4 space-y-4">
                            {listing.descriptionParagraphs.map((paragraph) => (
                                <p key={paragraph} className="m-0 whitespace-pre-line text-[16px] leading-[1.55] text-[#2f4359]">
                                    {paragraph}
                                </p>
                            ))}
                        </div>
                    </section>

                    <section className="fixed bottom-3 left-1/2 z-20 grid w-[calc(100%-16px)] max-w-[544px] -translate-x-1/2 grid-cols-2 gap-3 px-1">
                        <button
                            type="button"
                            onClick={handleShare}
                            className="flex h-[46px] items-center justify-center gap-2 rounded-full bg-white text-[14px] font-semibold uppercase tracking-[0.3px] text-[#2b3f53] shadow-[0_8px_18px_rgba(20,38,53,0.12)]"
                        >
                            <Share2 size={16} className="text-[#1d85d6]" />
                            Share
                        </button>
                        <button
                            type="button"
                            onClick={() =>
                                navigate(
                                    `/offline-listing/details/${encodeURIComponent(
                                        listing.id
                                    )}/form`
                                )
                            }
                            className="flex h-[46px] items-center justify-center gap-2 rounded-full bg-white text-[14px] font-semibold uppercase tracking-[0.3px] text-[#2b3f53] shadow-[0_8px_18px_rgba(20,38,53,0.12)]"
                        >
                            <MessageCircle size={16} className="text-[#21b44c]" />
                            WhatsApp
                        </button>
                    </section>
                </main>
            </div>
        </div>
    );
}

export default OfflineDetails;
